import {useEffect, useState} from 'react';
import {Controller, FieldError, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router-dom';
import {ArrowLeftOutlined} from '@ant-design/icons';
import {Button, Switch, theme} from 'antd';
import {FormShell} from 'components/form/FormShell';
import {RangeInput} from 'components/form/RangeInput';
import {OptionType, Select} from 'components/form/Select';
import {TextArea} from 'components/form/TextArea';
import {TextInput} from 'components/form/TextInput';
import {STATUS_OPTIONS} from 'helpers/constants';
import {usePrompt} from 'helpers/useBlocker';
import {checkIsAllDay, mapCarsToOptions} from 'helpers/utils';
import {observer} from 'mobx-react-lite';
import {browserHistory} from 'routing/browserHistory';
import {appState} from 'store/AppStore';
import {ServiceState} from 'store/services/ServiceState';
import {ServiceActionDto, ServiceStatus} from '../../../../../../PRMGApiClient';

export const ServiceEditView: React.FC = observer((): JSX.Element => {
  const {t} = useTranslation();
  const {token} = theme.useToken();
  const [state] = useState(() => new ServiceState());
  const [isAllDay, setIsAllDay] = useState(false);
  const {serviceId, vehicleId} = useParams();
  const allCarsOptions = mapCarsToOptions(appState.allCars);

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: {errors, isDirty},
  } = useForm<ServiceActionDto>({
    mode: 'onTouched',
    defaultValues: {
      carId: Number(vehicleId),
      required: true,
      status: ServiceStatus.Planned,
      startDateTime: new Date(),
      endDateTime: new Date(),
    },
  });
  const startDate = watch('startDateTime');
  const endDate = watch('endDateTime');

  useEffect(() => {
    state.loadServiceTypeList();
    serviceId && state.loadService(Number(serviceId));
  }, [state, serviceId]);

  useEffect(() => {
    if (serviceId) reset(state.item);
  }, [state.item, serviceId, reset, state.serviceTypeList]);

  useEffect(() => {
    setIsAllDay(checkIsAllDay(startDate, endDate));
  }, [startDate, endDate]);

  usePrompt(t('Are you sure? There are unsaved changes.'), serviceId ? isDirty : false);

  return (
    <>
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => browserHistory.back()}
        style={{color: token.colorTextDescription}}
        className="px-0"
      >
        {t('Back')}
      </Button>
      <div className="px-48 max-w-[92rem] mx-auto">
        <FormShell
          isDirty={isDirty}
          onReset={() => reset()}
          onSubmit={handleSubmit((payload) => {
            state.save(payload);
          })}
          onDelete={serviceId ? () => state.deleteService() : undefined}
          isLoading={state.isLoading}
        >
          <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2">
            {!serviceId && !vehicleId && (
              <Controller
                name="carId"
                control={control}
                rules={{required: true}}
                render={({field: {onChange, value}}) => (
                  <Select
                    id="carId"
                    className="col-start-1"
                    label={t('Search car')}
                    placeholder={t('Type mark, model or plate number...')}
                    options={allCarsOptions}
                    value={allCarsOptions.find((item) => item.value === value) ?? null}
                    required
                    error={errors.carId}
                    onChange={(option) => onChange((option as OptionType).value)}
                  />
                )}
              />
            )}
            <TextInput
              containerClassName="col-start-1"
              label={t('Service name')}
              error={errors.name}
              required
              {...register('name', {required: true})}
            />
            <Controller
              name="required"
              control={control}
              render={({field: {onChange, value}}) => (
                <div className="mb-3 col-start-1">
                  <Switch className="mr-2" checked={value !== null ? value : true} onChange={onChange} />
                  <span>{t('Service required')}</span>
                </div>
              )}
            />
            <div className="mb-3 col-start-1">
              <Switch className="mr-2" checked={isAllDay ?? false} onChange={(value) => setIsAllDay(value)} />
              <span>{t('All day')}</span>
            </div>
            <div className="grid grid-cols-1 gap-x-4 md:grid-cols-2 lg:grid-cols-2 col-span-2">
              <Controller
                name="startDateTime"
                control={control}
                rules={{required: true}}
                render={() => (
                  <RangeInput
                    id="startDateTime"
                    label={t('Start and end date')}
                    value={[startDate, endDate]}
                    required
                    isAllDay={isAllDay}
                    showTime
                    error={errors.startDateTime ?? errors.endDateTime}
                    onChange={(startDate, endDate) => {
                      setValue('startDateTime', startDate);
                      setValue('endDateTime', endDate);
                    }}
                  />
                )}
              />
            </div>
            <Controller
              name="serviceType"
              control={control}
              rules={{required: true}}
              render={({field: {onChange, value}}) => (
                <Select
                  id="type"
                  className="col-start-1"
                  label={t('Type')}
                  options={state.serviceTypesAsOptions}
                  value={state.serviceTypesAsOptions.find((item) => item.value === value?.id) ?? null}
                  error={errors.serviceType as FieldError}
                  required
                  onChange={(option) =>
                    onChange(
                      state.serviceTypes.find((serviceType) => {
                        return serviceType.id === (option as OptionType).value;
                      })
                    )
                  }
                />
              )}
            />
            <Controller
              name="status"
              control={control}
              rules={{required: true}}
              render={({field: {onChange, value}}) => (
                <Select
                  id="state"
                  className="col-start-1"
                  label={t('State')}
                  options={STATUS_OPTIONS}
                  value={STATUS_OPTIONS.find((item) => item.value === value) ?? null}
                  required
                  error={errors.status}
                  onChange={(option) => onChange((option as OptionType).value)}
                />
              )}
            />
            <div className="col-start-1">
              <Controller
                name="description"
                control={control}
                render={({field: {onChange, value}}) => (
                  <TextArea
                    label={t('Description')}
                    id={'description'}
                    value={value ?? ''}
                    placeholder={t('Description')}
                    onChange={onChange}
                  />
                )}
              />
            </div>
          </div>
        </FormShell>
      </div>
    </>
  );
});
